import { useEffect, useRef } from "react";
import { create } from "zustand";
import { Link, useParams } from "react-router-dom";
import { ChevronDown, Circle, Loader2, Mic, MicOff, Phone, PhoneIncoming, PhoneOff, PhoneOutgoing, X } from "lucide-react";
import { Button } from "@/frontend/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/frontend/components/ui/dropdown-menu";
import { callerLabel, useCallStore, type PublicCall } from "@/frontend/stores/callStore";
import { useCallEvents } from "@/frontend/hooks/useCallEvents";
import { useCallSounds, useFlashingTitle, type CallSound } from "@/frontend/hooks/useCallSounds";
import { formatCallDuration } from "@/frontend/lib/call-format";
import { cn } from "@/frontend/lib/utils";
import { ConversationAvatar } from "@/frontend/components/ConversationAvatar";
import { useSidebar } from "@/frontend/components/ui/sidebar";

// Alt+Shift+key while a call is on screen; letters avoid browser shortcuts on every platform
const SHORTCUTS = { answer: "KeyA", decline: "KeyD", mute: "KeyM", end: "KeyE" } as const;

function routingNote(call: PublicCall, userId: string | undefined) {
  if (!call.ringOwnerUserId || call.ringOwnerUserId !== userId) return null;
  if (call.routingReason === "ticket") return "Rings you first: you're assigned their open task";
  if (call.routingReason === "lead_owner") return "Rings you first: you own this lead";
  if (call.routingReason === "last_agent") return "Rings you first: you spoke to them last";
  return null;
}

const useMicPermissionStore = create<{ state: PermissionState | null }>(() => ({ state: null }));

// Whether the browser will still ask for the microphone; null where browsers can't tell
function useMicrophonePermission() {
  const state = useMicPermissionStore((store) => store.state);
  useEffect(() => {
    if (!navigator.permissions?.query) return;
    let status: PermissionStatus | null = null;
    const update = () => { if (status) useMicPermissionStore.setState({ state: status.state }); };
    void navigator.permissions.query({ name: "microphone" as PermissionName }).then((result) => {
      status = result;
      update();
      result.addEventListener("change", update);
    }).catch(() => undefined);
    return () => status?.removeEventListener("change", update);
  }, []);
  return state;
}

function CallerAvatar({ name, ringing }: { name: string; ringing: boolean }) {
  return (
    <span
      className={cn(
        "shrink-0 rounded-full",
        // A pulsing ring, not a fading face, marks a call that is ringing
        ringing && "animate-pulse ring-2 ring-emerald-500 ring-offset-2 ring-offset-card",
      )}
    >
      <ConversationAvatar name={name} platform="waba" size="sm" />
    </span>
  );
}

// Rows share the banners' layout: content held to max-w-5xl, buttons on their own line on phones
const ROW = "mx-auto flex max-w-5xl flex-wrap items-center gap-x-3 gap-y-2";
const ACTIONS = "flex basis-full items-center justify-end gap-2 sm:basis-auto";

function RingingRow({ call, waiting, orgId, userId }: { call: PublicCall; waiting: boolean; orgId: string; userId: string | undefined }) {
  const pending = useCallStore((state) => state.pending);
  const answerError = useCallStore((state) => state.answerError?.callId === call.id ? state.answerError.message : null);
  const now = useCallStore((state) => state.now);
  const { answer, dismiss, declineForEveryone } = useCallStore.getState();
  const name = callerLabel(call);
  const note = routingNote(call, userId);
  const micPrompt = useMicrophonePermission() === "prompt";
  const ringingFor = formatCallDuration(Math.max(0, Math.round((now - Date.parse(call.startedAt)) / 1000)));

  return (
    <div className={cn(ROW, "px-4 py-2.5")}>
      <CallerAvatar name={name} ringing />
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-300">
          <PhoneIncoming className="h-3.5 w-3.5" aria-hidden="true" />
          {waiting ? "Also calling" : "Incoming WhatsApp call"}
          <span className="font-normal tabular-nums text-muted-foreground">· {ringingFor}</span>
        </p>
        <p className="truncate text-sm">
          <Link to={`/org/${orgId}/app/inbox/${encodeURIComponent(call.conversationId)}`} className="font-semibold hover:underline">
            {name}
          </Link>
          <span className="text-muted-foreground"> to {call.businessName || call.businessPhone}</span>
        </p>
        {note && <p className="truncate text-xs text-muted-foreground">{note}</p>}
        {answerError && <p className="text-xs text-destructive" role="alert">{answerError}</p>}
        {!answerError && micPrompt && <p className="text-xs text-muted-foreground">Your browser will ask to use the microphone when you answer.</p>}
        {waiting && <p className="text-xs text-muted-foreground">End your call to answer this one.</p>}
      </div>
      <div className={ACTIONS}>
        <div className="flex">
          <Button
            variant="outline"
            size="sm"
            className="rounded-r-none"
            onClick={() => void dismiss(call.id)}
            disabled={pending}
            title={`Stop ringing for you; teammates can still answer (Alt+Shift+D)`}
          >
            <PhoneOff className="mr-1.5 h-4 w-4" aria-hidden="true" />
            Decline
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="rounded-l-none border-l-0 px-2" aria-label="More decline options" disabled={pending}>
                <ChevronDown className="h-4 w-4" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="z-[70]">
              <DropdownMenuItem onSelect={() => void declineForEveryone(call.id)}>
                Decline for everyone
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <Button
          size="sm"
          className="bg-emerald-600 text-white hover:bg-emerald-700"
          onClick={() => void answer(call.id)}
          disabled={pending || waiting}
          title={waiting ? "End your call to answer this one" : "Answer (Alt+Shift+A)"}
        >
          {pending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" /> : <Phone className="mr-1.5 h-4 w-4" aria-hidden="true" />}
          {answerError ? "Try again" : "Answer"}
        </Button>
      </div>
    </div>
  );
}

function ActiveRow({ orgId }: { orgId: string }) {
  const active = useCallStore((state) => state.active)!;
  const now = useCallStore((state) => state.now);
  const { hangUp, toggleMute } = useCallStore.getState();
  const { call } = active;
  const name = callerLabel(call);
  const ringingOut = call.direction === "outbound" && call.status === "ringing";
  const status = active.reconnecting ? "Reconnecting…"
    : call.status === "connected" && active.connectedAtLocal
      ? formatCallDuration(Math.max(0, Math.round((now - active.connectedAtLocal) / 1000)))
      : ringingOut ? "Ringing…" : "Connecting…";

  return (
    <div className={cn(ROW, "px-4 py-2.5")}>
      <CallerAvatar name={name} ringing={false} />
      <div className="min-w-0 flex-1">
        {/* Announce the call state, not the ticking timer */}
        <span className="sr-only" aria-live="polite">
          {active.reconnecting ? "Reconnecting" : call.status === "connected" ? `On a call with ${name}` : ringingOut ? `Calling ${name}` : "Connecting"}
        </span>
        <p className={cn("flex items-center gap-1.5 text-xs font-medium tabular-nums", active.reconnecting ? "text-amber-700 dark:text-amber-300" : "text-emerald-700 dark:text-emerald-300")} aria-hidden="true">
          {call.direction === "outbound" ? <PhoneOutgoing className="h-3.5 w-3.5" /> : <Phone className="h-3.5 w-3.5" />}
          {status}
          {call.recordingRequested && (
            <span className="ml-1 inline-flex items-center gap-1 font-normal text-red-600 dark:text-red-400">
              <Circle className="h-2 w-2 animate-pulse fill-current" />
              Recording
            </span>
          )}
        </p>
        <p className="truncate text-sm">
          <Link to={`/org/${orgId}/app/inbox/${encodeURIComponent(call.conversationId)}`} className="font-semibold hover:underline">
            {name}
          </Link>
          <span className="text-muted-foreground"> · {call.businessName || call.businessPhone}</span>
        </p>
      </div>
      <div className={ACTIONS}>
        {!ringingOut && (
          <Button
            variant="outline"
            size="sm"
            onClick={toggleMute}
            title={`${active.muted ? "Unmute" : "Mute"} (Alt+Shift+M)`}
            className={cn(active.muted && "border-amber-300 bg-amber-100 text-amber-800 hover:bg-amber-200 dark:border-amber-500/40 dark:bg-amber-500/15 dark:text-amber-200")}
          >
            {active.muted ? <MicOff className="mr-1.5 h-4 w-4" aria-hidden="true" /> : <Mic className="mr-1.5 h-4 w-4" aria-hidden="true" />}
            {active.muted ? "Unmute" : "Mute"}
          </Button>
        )}
        <Button variant="destructive" size="sm" onClick={() => void hangUp()} title={`${ringingOut ? "Cancel" : "End call"} (Alt+Shift+E)`}>
          <PhoneOff className="mr-1.5 h-4 w-4" aria-hidden="true" />
          {ringingOut ? "Cancel" : "End call"}
        </Button>
      </div>
    </div>
  );
}

function DialingRow() {
  const dialing = useCallStore((state) => state.dialing)!;
  const { cancelDialing } = useCallStore.getState();
  return (
    <div className={cn(ROW, "px-4 py-2.5")}>
      <CallerAvatar name={dialing.label} ringing={false} />
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-300">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          Starting the call…
        </p>
        <p className="truncate text-sm font-semibold" aria-live="polite">Calling {dialing.label}</p>
      </div>
      <div className={ACTIONS}>
        <Button variant="destructive" size="sm" onClick={cancelDialing}>
          <PhoneOff className="mr-1.5 h-4 w-4" aria-hidden="true" />
          Cancel
        </Button>
      </div>
    </div>
  );
}

function EndedRow({ orgId }: { orgId: string }) {
  const ended = useCallStore((state) => state.ended)!;
  const { clearEnded } = useCallStore.getState();
  const { call, seconds } = ended;
  const duration = call.status === "completed" || call.status === "connected" ? formatCallDuration(seconds) : "";
  return (
    <div className={cn(ROW, "flex-nowrap bg-muted/40 px-4 py-2.5")} role="status">
      <PhoneOff className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <p className="min-w-0 flex-1 truncate text-sm">
        Call ended{duration ? ` · ${duration}` : ""} · <span className="font-medium">{callerLabel(call)}</span>
      </p>
      <Button variant="ghost" size="sm" asChild>
        <Link to={`/org/${orgId}/app/inbox/${encodeURIComponent(call.conversationId)}`} onClick={clearEnded}>Open chat</Link>
      </Button>
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={clearEnded} aria-label="Dismiss">
        <X className="h-4 w-4" aria-hidden="true" />
      </Button>
    </div>
  );
}

/**
 * Incoming, outgoing and live WhatsApp calls, as a bar across the top of the app. It sits in the page
 * instead of floating, so it never covers the chat composer, and above dialogs so a call can be answered
 * from anywhere.
 */
export function CallBar({ userId }: { userId: string | undefined }) {
  const { orgId } = useParams<{ orgId: string }>();
  useCallEvents(orgId);
  const ringing = useCallStore((state) => state.ringing);
  const active = useCallStore((state) => state.active);
  const dialing = useCallStore((state) => state.dialing);
  const ended = useCallStore((state) => state.ended);
  const pending = useCallStore((state) => state.pending);
  const visible = Boolean(orgId && (ringing.length > 0 || active || dialing || ended));

  const sound: CallSound = pending && !active && !dialing ? null
    : !active && ringing.length > 0 ? "ring"
      : active?.call.direction === "outbound" && active.call.status === "ringing" ? "ringback"
        : active && ringing.length > 0 ? "waiting"
          : null;
  useCallSounds(sound);
  useFlashingTitle(!active && ringing.length > 0 ? `Call from ${callerLabel(ringing[0])}` : null);

  // The timers on screen tick once a second
  const ticking = ringing.length > 0 || Boolean(active);
  useEffect(() => {
    if (!ticking) return;
    useCallStore.setState({ now: Date.now() });
    const timer = window.setInterval(() => useCallStore.setState({ now: Date.now() }), 1000);
    return () => window.clearInterval(timer);
  }, [ticking]);

  // The mobile menu sheet would sit under the bar, so it closes when a call comes in
  const { setOpenMobile } = useSidebar();
  const ringingCount = ringing.length;
  useEffect(() => {
    if (ringingCount > 0) setOpenMobile(false);
  }, [ringingCount, setOpenMobile]);

  // Focus goes back where it was once the bar closes
  const returnFocus = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (visible) {
      returnFocus.current ??= document.activeElement instanceof HTMLElement ? document.activeElement : null;
      return;
    }
    if (returnFocus.current?.isConnected) returnFocus.current.focus();
    returnFocus.current = null;
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (!event.altKey || !event.shiftKey || event.metaKey || event.ctrlKey) return;
      const state = useCallStore.getState();
      const first = state.ringing[0];
      if (event.code === SHORTCUTS.answer && first && !state.active) void state.answer(first.id);
      else if (event.code === SHORTCUTS.decline && first) void state.dismiss(first.id);
      else if (event.code === SHORTCUTS.mute && state.active) state.toggleMute();
      else if (event.code === SHORTCUTS.end && state.active) void state.hangUp();
      else return;
      event.preventDefault();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [visible]);

  if (!visible || !orgId) return null;
  const first = ringing[0];
  // Above dialogs only while a call rings or is up, so it can always be answered or hung up
  const live = ringing.length > 0 || Boolean(active) || Boolean(dialing);

  return (
    <section
      className={cn(
        // Like the app's banners, with an emerald accent for a live call
        "pointer-events-auto shrink-0 divide-y divide-border border-b border-border bg-card",
        live && "relative z-[60] shadow-[inset_3px_0_0_0_theme(colors.emerald.500)]",
      )}
      aria-label="WhatsApp calls"
    >
      {first && !active && (
        <p className="sr-only" role="alert">Incoming WhatsApp call from {callerLabel(first)}</p>
      )}
      {dialing && !active && <DialingRow />}
      {active && <ActiveRow orgId={orgId} />}
      {ringing.map((call) => (
        <RingingRow key={call.id} call={call} waiting={Boolean(active)} orgId={orgId} userId={userId} />
      ))}
      {ended && !active && !dialing && ringing.length === 0 && <EndedRow orgId={orgId} />}
    </section>
  );
}
